import { TFunction } from 'i18next';
import { Trans } from 'next-i18next/pages';
import Link from 'next/link';

import { FaqObject } from '@pflegia/ui/shared/landing-page/faq-page-json-ld/faq-page-json-ld-base';

import { stripFormattingTagsForSchema } from '../constants/faq';
import { LlpFaqPageCase, SelectedLlpFaq } from './select-llp-faqs';

type DynamicFaqParams = {
  employerNames?: string;
  location?: string;
  salaryMax?: string;
  salaryMin?: string;
  translatedJobTitle?: string;
  translatedJobTitlePlural?: string;
  vacancyCount?: string;
};

interface BuildDynamicLlpFaqsProps {
  faqs: SelectedLlpFaq[];
  pageCase: LlpFaqPageCase;
  params: DynamicFaqParams;
  tJobListings: TFunction;
}

const resolveCopyKey = (faq: SelectedLlpFaq): string => {
  if (faq.category === 'salary') return faq.salaryUsedFallback ? 'salaryFallback' : 'salary';
  if (faq.category === 'professionOrTraining') return faq.linkIntent ?? 'profession';
  return faq.category;
};

const getSchemaText = (value: string) => stripFormattingTagsForSchema(value).trim();

export const buildDynamicLlpFaqs = ({ faqs, pageCase, params, tJobListings }: BuildDynamicLlpFaqsProps): FaqObject[] =>
  faqs.flatMap(faq => {
    const keyBase = `questionsAndAnswers.dynamicFaqs.${pageCase}.${resolveCopyKey(faq)}`;
    const questionKey = `${keyBase}.question`;
    const answerKey = `${keyBase}.answer`;

    if (tJobListings(questionKey) === questionKey || tJobListings(answerKey) === answerKey) return [];

    return [
      {
        question: <Trans i18nKey={questionKey} t={tJobListings} values={params} components={{ strong: <strong /> }} />,
        answer: (
          <Trans
            i18nKey={answerKey}
            t={tJobListings}
            values={params}
            components={{
              strong: <strong />,
              faqLink: faq.link ? (
                <Link onClick={event => event.stopPropagation()} href={faq.link} className='text-primary hover:underline' />
              ) : (
                <span />
              ),
            }}
          />
        ),
        questionForSchema: getSchemaText(tJobListings(questionKey, params)),
        answerForSchema: getSchemaText(tJobListings(answerKey, params)),
      },
    ];
  });
